// M20 ad templates (owner brief 2026-10-12, replaces the M14 set, which lives on hidden as "Classic"):
// modern food advertising in 5 style families — Clean, Bold Street, Elegant, Fresh Market, Promo — each with a
// 1:1 post, a 4:5 post, a 9:16 story and a delivery banner, plus a menu / special / promo where it fits.
// Rules every template keeps (tests/unit/templates.test.ts checks them):
//   - the dish is the hero: a full-bleed photo (text on a soft gradient or a clean band) or the cut-out dish
//     floating big on a solid colour with a soft natural shadow; never a small boxed photo;
//   - SPANISH ONLY (owner decision); at most 3 ad texts besides the restaurant / contact (menus list dishes);
//   - no text, nor its shrink-to-fit minimum, under 3% of the ad's short side (Builder.minText);
//   - at most 2 colours + neutrals: the brand's primary and/or secondary (owner 2026-10-12: brown for prices,
//     small headings and dark backgrounds) or the photo tone; text colours come from the brand by contrast
//     (onPrimary, accentText … in dsl.ts) so every brand meets WCAG AA without changing its values;
//   - at most 2 bundled OFL fonts per template; the logo small in a corner;
//   - stories keep the top 14% and the bottom 20% clear of text and logo;
//   - menus: a clean grid, prices right-aligned in one column, the photo used once.
// All art is type, colour, the photo and plain rectangles; nothing is copied from anyone's design.
import type { Builder, Col, TemplateDef, TextOpts } from './dsl';
import { esOnly } from '../ad/fields';

const bi = (es: string, en: string) => ({ es, en });
const INK = '#1a1a1a';
const MUTED = '#6b6b6b';

type T = Omit<TextOpts, 'role'>;
/** A Spanish ad text bound to a field. */
const tx = (b: Builder, role: TextOpts['role'], x: number, y: number, w: number, h: number, o: T) =>
  b.text(x, y, w, h, { maxLines: 1, ...o, role, lang: 'es' });

/** Soft shadow under a floating cut-out (follows the dish's own outline). */
const SOFT = { blur: 46, y: 28, opacity: 0.24 };
const DEEP = { blur: 60, y: 34, opacity: 0.5 };

/** A dish menu: names left, prices right-aligned in one column, even leading. */
function menuList(
  b: Builder,
  n: number,
  x: number,
  y: number,
  w: number,
  row: number,
  o: {
    family: string;
    size: number;
    weight?: number;
    color: Col;
    price: Col;
    priceFamily?: string;
    rule?: Col;
  },
) {
  const pw = o.size * 4.4;
  for (let i = 0; i < n; i++) {
    const yy = y + i * row;
    b.text(x, yy, w - pw - o.size, o.size * 1.3, {
      role: 'itemName',
      item: i,
      lang: 'es',
      family: o.family,
      weight: o.weight ?? 400,
      size: o.size,
      color: o.color,
      maxLines: 1,
    });
    b.text(x + w - pw, yy, pw, o.size * 1.3, {
      role: 'itemPrice',
      item: i,
      family: o.priceFamily ?? o.family,
      weight: 700,
      size: o.size,
      color: o.price,
      align: 'right',
      maxLines: 1,
    });
    if (o.rule && i < n - 1)
      b.line(x, yy + row - (row - o.size * 1.3) / 2, x + w, yy + row - (row - o.size * 1.3) / 2, o.rule, 1);
  }
}

/** Dark-to-clear band for text over a full-bleed photo. */
const shade = (b: Builder, y: number, h: number, a = 0.82, c: 'black' | 'toneDark' | 'secondary' = 'black') =>
  b.grad(0, y, b.W, h, [
    [0, [c, 0]],
    [0.55, [c, a * 0.75]],
    [1, [c, a]],
  ]);

export const FAMILY_TEMPLATES: TemplateDef[] = [
  // ════════════════════════ CLEAN — white, lots of space, thin sans (Montserrat) ════════════════════════
  {
    id: 'clean-post',
    family: 'clean',
    slot: 'cutout',
    category: 'post',
    format: 'ig-square',
    name: bi('Limpio — platillo flotante', 'Clean — floating dish'),
    build: (b) => {
      b.bg('white');
      tx(b, 'dishName', 90, 80, 900, 90, {
        family: 'Montserrat',
        weight: 400,
        size: 64,
        color: INK,
        align: 'center',
      });
      tx(b, 'description', 90, 172, 900, 46, {
        family: 'Montserrat',
        weight: 400,
        size: 34,
        color: MUTED,
        align: 'center',
      });
      b.photo(110, 250, 860, 570, { fit: 'contain', zoom: 0.9, shadow: SOFT });
      tx(b, 'price', 640, 960, 380, 64, {
        family: 'Montserrat',
        weight: 700,
        size: 50,
        color: 'accentText',
        align: 'right',
      });
      b.logo(40, 830, 220, 220, { onLight: true });
    },
  },
  {
    id: 'clean-portrait',
    family: 'clean',
    slot: 'cutout',
    category: 'post',
    format: 'ig-portrait',
    name: bi('Limpio — vertical', 'Clean — portrait'),
    build: (b) => {
      b.bg('white');
      tx(b, 'dishName', 80, 100, 920, 190, {
        family: 'Montserrat',
        weight: 400,
        size: 80,
        color: INK,
        maxLines: 2,
        leading: 1.08,
      });
      tx(b, 'description', 80, 300, 920, 50, { family: 'Montserrat', weight: 400, size: 36, color: MUTED });
      b.photo(60, 380, 960, 720, { fit: 'contain', zoom: 0.92, shadow: SOFT });
      tx(b, 'price', 80, 1220, 500, 70, { family: 'Montserrat', weight: 700, size: 56, color: 'accentText' });
      b.logo(800, 1110, 220, 220, { onLight: true });
    },
  },
  {
    id: 'clean-story',
    family: 'clean',
    slot: 'cutout',
    category: 'story',
    format: 'story',
    name: bi('Limpio — historia', 'Clean — story'),
    build: (b) => {
      b.bg('white');
      tx(b, 'dishName', 90, 300, 900, 200, {
        family: 'Montserrat',
        weight: 400,
        size: 88,
        color: INK,
        align: 'center',
        maxLines: 2,
        leading: 1.08,
      });
      tx(b, 'description', 90, 510, 900, 54, {
        family: 'Montserrat',
        weight: 400,
        size: 40,
        color: MUTED,
        align: 'center',
      });
      b.photo(60, 590, 960, 760, { fit: 'contain', zoom: 0.92, shadow: SOFT });
      tx(b, 'price', 290, 1370, 500, 76, {
        family: 'Montserrat',
        weight: 700,
        size: 62,
        color: 'accentText',
        align: 'center',
      });
      b.logo(60, 1364, 172, 172, { onLight: true });
    },
  },
  {
    id: 'clean-delivery',
    family: 'clean',
    slot: 'cutout',
    category: 'delivery',
    format: 'delivery',
    name: bi('Limpio — banner de delivery', 'Clean — delivery banner'),
    build: (b) => {
      b.bg('white');
      b.logo(70, 30, 200, 200, { onLight: true });
      tx(b, 'dishName', 70, 240, 480, 150, {
        family: 'Montserrat',
        weight: 400,
        size: 62,
        color: INK,
        maxLines: 2,
        leading: 1.08,
      });
      tx(b, 'description', 70, 400, 480, 70, {
        family: 'Montserrat',
        weight: 400,
        size: 28,
        color: MUTED,
        maxLines: 2,
      });
      tx(b, 'price', 70, 500, 400, 60, { family: 'Montserrat', weight: 700, size: 46, color: 'accentText' });
      b.photo(540, 30, 640, 740, { fit: 'contain', zoom: 0.92, shadow: SOFT });
    },
  },
  {
    id: 'clean-post-full',
    family: 'clean',
    slot: 'full',
    category: 'post',
    format: 'ig-square',
    name: bi('Limpio — foto completa con franja', 'Clean — full photo with band'),
    build: (b) => {
      b.bg('white');
      b.photo(0, 0, 1080, 810);
      b.logo(40, 30, 220, 220);
      tx(b, 'dishName', 60, 824, 680, 124, {
        family: 'Montserrat',
        weight: 400,
        size: 58,
        color: INK,
        maxLines: 2,
        leading: 1.06,
      });
      tx(b, 'description', 60, 960, 680, 46, { family: 'Montserrat', weight: 400, size: 33, color: MUTED });
      tx(b, 'price', 740, 832, 280, 70, {
        family: 'Montserrat',
        weight: 700,
        size: 54,
        color: 'accentText',
        align: 'right',
      });
    },
  },
  {
    id: 'clean-portrait-full',
    family: 'clean',
    slot: 'full',
    category: 'post',
    format: 'ig-portrait',
    name: bi('Limpio — foto completa vertical', 'Clean — full photo portrait'),
    build: (b) => {
      b.bg('white');
      b.photo(0, 0, 1080, 1350);
      b.grad(0, 760, 1080, 590, [
        [0, ['white', 0]],
        [0.5, ['white', 0.9]],
        [1, ['white', 1]],
      ]);
      b.logo(40, 30, 220, 220);
      tx(b, 'dishName', 80, 1060, 920, 96, { family: 'Montserrat', weight: 400, size: 76, color: INK });
      tx(b, 'description', 80, 1160, 700, 50, { family: 'Montserrat', weight: 400, size: 36, color: MUTED });
      tx(b, 'price', 760, 1158, 240, 60, {
        family: 'Montserrat',
        weight: 700,
        size: 50,
        color: 'accentText',
        align: 'right',
      });
    },
  },
  {
    id: 'clean-menu',
    family: 'clean',
    slot: 'full',
    category: 'menu',
    format: 'letter',
    name: bi('Limpio — menú impreso', 'Clean — printed menu'),
    build: (b) => {
      b.bg('white');
      b.text(60, 46, 492, 40, {
        role: 'restaurant',
        family: 'Montserrat',
        weight: 700,
        size: 30,
        color: INK,
        align: 'center',
        caps: true,
        tracking: 120,
        maxLines: 1,
      });
      b.photo(60, 104, 492, 150, { shape: 'rounded', r: 6 });
      menuList(b, 8, 60, 286, 492, 46, {
        family: 'Montserrat',
        size: 20,
        color: INK,
        price: 'accentText',
        rule: ['black', 0.08],
      });
      b.text(60, 744, 380, 26, {
        role: 'contact',
        family: 'Montserrat',
        size: 19,
        color: MUTED,
        maxLines: 1,
      });
      b.logo(452, 662, 100, 100, { onLight: true });
    },
  },
  {
    id: 'clean-board',
    family: 'clean',
    slot: 'full',
    category: 'board',
    format: 'board-h',
    name: bi('Limpio — pantalla de menú', 'Clean — menu board'),
    build: (b) => {
      b.bg('white');
      b.photo(0, 0, 820, 1080);
      b.text(920, 90, 660, 70, {
        role: 'restaurant',
        family: 'Montserrat',
        weight: 700,
        size: 52,
        color: INK,
        caps: true,
        tracking: 120,
        maxLines: 1,
      });
      menuList(b, 6, 920, 300, 900, 112, {
        family: 'Montserrat',
        size: 46,
        color: INK,
        price: 'accentText',
        rule: ['black', 0.08],
      });
      b.logo(1620, 30, 240, 240, { onLight: true });
    },
  },

  // ════════════════════ BOLD STREET — big condensed type (Anton) + Montserrat, ONE strong brand colour ════════════════════
  {
    id: 'bold-post',
    family: 'bold',
    slot: 'full',
    category: 'post',
    format: 'ig-square',
    name: bi('Urbano — foto completa', 'Bold Street — full photo'),
    build: (b) => {
      b.bg('secondary');
      b.photo(0, 0, 1080, 1080);
      shade(b, 520, 560, 0.85, 'secondary');
      b.rect(820, 0, 260, 150, { fill: 'primary' });
      tx(b, 'price', 820, 32, 260, 86, { family: 'Anton', size: 72, color: 'onPrimary', align: 'center' });
      b.logo(40, 30, 220, 220);
      tx(b, 'dishName', 60, 730, 960, 210, {
        maxLines: 2,
        family: 'Anton',
        size: 124,
        color: 'onSecondary',
        caps: true,
        leading: 1,
      });
      tx(b, 'description', 60, 950, 960, 48, {
        family: 'Montserrat',
        weight: 700,
        size: 34,
        color: 'onSecondarySmall',
      });
    },
  },
  {
    id: 'bold-portrait',
    family: 'bold',
    slot: 'cutout',
    category: 'post',
    format: 'ig-portrait',
    name: bi('Urbano — platillo sobre color', 'Bold Street — dish on colour'),
    build: (b) => {
      b.bg('primary');
      tx(b, 'dishName', 60, 70, 960, 320, {
        family: 'Anton',
        size: 160,
        color: 'onPrimary',
        caps: true,
        maxLines: 2,
        leading: 0.98,
      });
      b.photo(40, 380, 1000, 720, { fit: 'contain', zoom: 0.95, shadow: DEEP });
      b.rect(60, 1200, 330, 110, { fill: 'secondary' });
      tx(b, 'price', 60, 1214, 330, 84, { family: 'Anton', size: 72, color: 'onSecondary', align: 'center' });
      b.logo(820, 1110, 220, 220);
    },
  },
  {
    id: 'bold-story',
    family: 'bold',
    slot: 'full',
    category: 'story',
    format: 'story',
    name: bi('Urbano — historia', 'Bold Street — story'),
    build: (b) => {
      b.bg('secondary');
      b.photo(0, 0, 1080, 1920);
      b.rect(0, 1090, 1080, 446, { fill: 'primary' });
      b.logo(60, 280, 240, 240);
      tx(b, 'dishName', 60, 1130, 960, 250, {
        family: 'Anton',
        size: 120,
        color: 'onPrimary',
        caps: true,
        maxLines: 2,
        leading: 1,
      });
      tx(b, 'description', 60, 1390, 640, 54, {
        family: 'Montserrat',
        weight: 700,
        size: 38,
        color: 'onPrimarySmall',
      });
      tx(b, 'price', 700, 1380, 320, 96, { family: 'Anton', size: 84, color: 'onPrimary', align: 'right' });
    },
  },
  {
    id: 'bold-delivery',
    family: 'bold',
    slot: 'cutout',
    category: 'delivery',
    format: 'delivery',
    name: bi('Urbano — banner de delivery', 'Bold Street — delivery banner'),
    build: (b) => {
      b.bg('primary');
      b.logo(60, 24, 200, 200);
      tx(b, 'dishName', 60, 240, 520, 260, {
        family: 'Anton',
        size: 110,
        color: 'onPrimary',
        caps: true,
        maxLines: 2,
        leading: 1,
      });
      b.rect(60, 530, 260, 96, { fill: 'secondary' });
      tx(b, 'price', 60, 542, 260, 72, { family: 'Anton', size: 60, color: 'onSecondary', align: 'center' });
      b.photo(520, 20, 680, 760, { fit: 'contain', zoom: 0.95, shadow: DEEP });
    },
  },
  {
    id: 'bold-special',
    family: 'bold',
    slot: 'cutout',
    category: 'special',
    format: 'ig-square',
    name: bi('Urbano — especial del día', 'Bold Street — daily special'),
    ad: { headline: esOnly('Especial de hoy') },
    build: (b) => {
      b.bg('secondary');
      tx(b, 'headline', 60, 64, 960, 56, {
        family: 'Montserrat',
        weight: 800,
        size: 52,
        color: 'accentOnSecondary',
        caps: true,
        tracking: 160,
      });
      tx(b, 'dishName', 60, 126, 960, 150, {
        maxLines: 2,
        family: 'Anton',
        size: 116,
        color: 'onSecondary',
        caps: true,
        leading: 1,
      });
      b.photo(90, 290, 900, 540, { fit: 'contain', zoom: 0.95, shadow: DEEP });
      tx(b, 'price', 640, 960, 380, 84, {
        family: 'Anton',
        size: 72,
        color: 'accentOnSecondary',
        align: 'right',
      });
      b.logo(40, 840, 220, 220);
    },
  },
  {
    id: 'bold-board',
    family: 'bold',
    slot: 'full',
    category: 'board',
    format: 'board-h',
    name: bi('Urbano — pantalla de menú', 'Bold Street — menu board'),
    build: (b) => {
      b.bg('secondary');
      b.photo(1180, 0, 740, 1080);
      b.rect(1180, 0, 14, 1080, { fill: 'primary' });
      b.text(90, 80, 1000, 120, {
        role: 'restaurant',
        family: 'Anton',
        size: 104,
        color: 'onSecondarySmall',
        caps: true,
        maxLines: 1,
      });
      menuList(b, 6, 90, 270, 1000, 105, {
        family: 'Anton',
        size: 56,
        color: 'onSecondarySmall',
        price: 'onSecondarySmall',
        priceFamily: 'Anton',
      });
      b.logo(90, 875, 190, 190);
    },
  },

  // ════════════════════ ELEGANT — dark, serif headline (Playfair Display) + Montserrat caps, restrained ════════════════════
  {
    id: 'elegant-post',
    family: 'elegant',
    slot: 'full',
    category: 'post',
    format: 'ig-square',
    name: bi('Elegante — foto completa', 'Elegant — full photo'),
    build: (b) => {
      b.bg('secondary');
      b.photo(0, 0, 1080, 1080);
      shade(b, 480, 600, 0.88, 'secondary');
      b.logo(430, 30, 220, 220);
      tx(b, 'dishName', 80, 800, 920, 96, {
        family: 'Playfair Display',
        weight: 400,
        size: 78,
        color: 'onSecondary',
        align: 'center',
      });
      b.line(500, 912, 580, 912, 'primary', 2);
      tx(b, 'description', 80, 930, 920, 46, {
        family: 'Montserrat',
        weight: 400,
        size: 33,
        color: 'onSecondarySmall',
        align: 'center',
      });
      tx(b, 'price', 340, 985, 400, 50, {
        family: 'Montserrat',
        weight: 400,
        size: 36,
        color: 'onSecondarySmall',
        align: 'center',
        tracking: 120,
      });
    },
  },
  {
    id: 'elegant-portrait',
    family: 'elegant',
    slot: 'cutout',
    category: 'post',
    format: 'ig-portrait',
    name: bi('Elegante — platillo sobre fondo oscuro', 'Elegant — dish on dark'),
    build: (b) => {
      b.bg('secondary');
      b.logo(430, 30, 220, 220);
      b.photo(60, 260, 960, 740, { fit: 'contain', zoom: 0.9, shadow: { blur: 70, y: 36, opacity: 0.6 } });
      tx(b, 'dishName', 80, 1010, 920, 110, {
        family: 'Playfair Display',
        weight: 400,
        size: 84,
        color: 'onSecondary',
        align: 'center',
      });
      b.line(500, 1136, 580, 1136, 'primary', 2);
      tx(b, 'description', 80, 1158, 920, 48, {
        family: 'Montserrat',
        weight: 400,
        size: 34,
        color: 'onSecondarySmall',
        align: 'center',
      });
      tx(b, 'price', 340, 1230, 400, 56, {
        family: 'Montserrat',
        weight: 400,
        size: 40,
        color: 'onSecondarySmall',
        align: 'center',
        tracking: 120,
      });
    },
  },
  {
    id: 'elegant-story',
    family: 'elegant',
    slot: 'full',
    category: 'story',
    format: 'story',
    name: bi('Elegante — historia', 'Elegant — story'),
    build: (b) => {
      b.bg('secondary');
      b.photo(0, 0, 1080, 1920);
      b.grad(0, 860, 1080, 1060, [
        [0, ['secondary', 0]],
        [0.35, ['secondary', 0.8]],
        [1, ['secondary', 0.95]],
      ]);
      b.logo(420, 280, 240, 240);
      tx(b, 'dishName', 90, 1170, 900, 210, {
        family: 'Playfair Display',
        weight: 400,
        size: 96,
        color: 'onSecondary',
        align: 'center',
        maxLines: 2,
        leading: 1.06,
      });
      b.line(500, 1398, 580, 1398, 'primary', 2);
      tx(b, 'description', 90, 1420, 900, 52, {
        family: 'Montserrat',
        weight: 400,
        size: 36,
        color: 'onSecondarySmall',
        align: 'center',
      });
      tx(b, 'price', 340, 1478, 400, 54, {
        family: 'Montserrat',
        weight: 400,
        size: 40,
        color: 'onSecondarySmall',
        align: 'center',
        tracking: 120,
      });
    },
  },
  {
    id: 'elegant-delivery',
    family: 'elegant',
    slot: 'cutout',
    category: 'delivery',
    format: 'delivery',
    name: bi('Elegante — banner de delivery', 'Elegant — delivery banner'),
    build: (b) => {
      b.bg('secondary');
      b.logo(70, 30, 200, 200);
      tx(b, 'dishName', 70, 250, 500, 160, {
        family: 'Playfair Display',
        weight: 400,
        size: 66,
        color: 'onSecondarySmall',
        maxLines: 2,
        leading: 1.06,
      });
      b.line(70, 430, 150, 430, 'primary', 2);
      tx(b, 'description', 70, 452, 480, 70, {
        family: 'Montserrat',
        weight: 400,
        size: 26,
        color: 'onSecondarySmall',
        maxLines: 2,
      });
      tx(b, 'price', 70, 540, 400, 50, {
        family: 'Montserrat',
        weight: 400,
        size: 36,
        color: 'onSecondarySmall',
        tracking: 120,
      });
      b.photo(540, 30, 640, 740, { fit: 'contain', zoom: 0.9, shadow: { blur: 60, y: 30, opacity: 0.6 } });
    },
  },
  {
    id: 'elegant-special',
    family: 'elegant',
    slot: 'cutout',
    category: 'special',
    format: 'ig-square',
    name: bi('Elegante — sugerencia del chef', 'Elegant — chef’s suggestion'),
    ad: { headline: esOnly('Sugerencia del chef') },
    build: (b) => {
      b.bg('secondary');
      tx(b, 'headline', 90, 70, 900, 46, {
        family: 'Montserrat',
        weight: 400,
        size: 33,
        color: 'onSecondarySmall',
        align: 'center',
        caps: true,
        tracking: 220,
      });
      tx(b, 'dishName', 90, 124, 900, 96, {
        family: 'Playfair Display',
        weight: 400,
        size: 78,
        color: 'onSecondary',
        align: 'center',
      });
      b.photo(110, 240, 860, 590, { fit: 'contain', zoom: 0.9, shadow: { blur: 70, y: 34, opacity: 0.6 } });
      tx(b, 'price', 340, 970, 400, 54, {
        family: 'Montserrat',
        weight: 400,
        size: 40,
        color: 'onSecondarySmall',
        align: 'center',
        tracking: 120,
      });
      b.logo(40, 840, 220, 220);
    },
  },
  {
    id: 'elegant-menu',
    family: 'elegant',
    slot: 'full',
    category: 'menu',
    format: 'letter',
    name: bi('Elegante — menú impreso', 'Elegant — printed menu'),
    build: (b) => {
      b.bg('white');
      b.text(60, 44, 492, 46, {
        role: 'restaurant',
        family: 'Playfair Display',
        weight: 400,
        size: 36,
        color: INK,
        align: 'center',
        maxLines: 1,
      });
      b.line(276, 100, 336, 100, 'primary', 1.5);
      b.photo(197, 118, 218, 146, { shape: 'ellipse' });
      menuList(b, 8, 70, 290, 472, 46, {
        family: 'Playfair Display',
        size: 20,
        color: INK,
        price: 'accentText',
        priceFamily: 'Montserrat',
      });
      b.text(60, 744, 380, 26, {
        role: 'contact',
        family: 'Montserrat',
        size: 19,
        color: MUTED,
        maxLines: 1,
      });
      b.logo(452, 662, 100, 100, { onLight: true });
    },
  },
  {
    id: 'elegant-board',
    family: 'elegant',
    slot: 'full',
    category: 'board',
    format: 'board-v',
    name: bi('Elegante — pantalla vertical', 'Elegant — vertical menu board'),
    build: (b) => {
      b.bg('secondary');
      b.photo(0, 0, 1080, 760);
      b.grad(0, 560, 1080, 200, [
        [0, ['secondary', 0]],
        [1, ['secondary', 1]],
      ]);
      b.text(90, 800, 900, 90, {
        role: 'restaurant',
        family: 'Playfair Display',
        weight: 400,
        size: 72,
        color: 'onSecondary',
        align: 'center',
        maxLines: 1,
      });
      b.line(500, 908, 580, 908, 'primary', 2);
      menuList(b, 6, 110, 980, 860, 120, {
        family: 'Playfair Display',
        size: 46,
        color: 'onSecondarySmall',
        price: 'onSecondarySmall',
        priceFamily: 'Montserrat',
        rule: ['white', 0.12],
      });
      b.logo(420, 1660, 240, 240);
    },
  },

  // ════════════════════ FRESH MARKET — light natural tones, Poppins + a little Caveat ════════════════════
  {
    id: 'fresh-post',
    family: 'fresh',
    slot: 'cutout',
    category: 'post',
    format: 'ig-square',
    name: bi('Mercado fresco — platillo flotante', 'Fresh Market — floating dish'),
    ad: { tagline: esOnly('recién hecho') },
    build: (b) => {
      b.bg('toneLight');
      tx(b, 'tagline', 70, 64, 600, 70, { family: 'Caveat', weight: 700, size: 60, color: 'accentText' });
      tx(b, 'dishName', 70, 134, 940, 84, { family: 'Poppins', weight: 600, size: 66, color: 'toneDark' });
      b.photo(110, 240, 860, 580, { fit: 'contain', zoom: 0.92, shadow: { ...SOFT, color: 'toneDark' } });
      tx(b, 'price', 640, 966, 380, 64, {
        family: 'Poppins',
        weight: 600,
        size: 50,
        color: 'accentText',
        align: 'right',
      });
      b.logo(40, 830, 220, 220, { onLight: true });
    },
  },
  {
    id: 'fresh-portrait',
    family: 'fresh',
    slot: 'full',
    category: 'post',
    format: 'ig-portrait',
    name: bi('Mercado fresco — foto con panel', 'Fresh Market — photo with panel'),
    ad: { tagline: esOnly('de temporada') },
    build: (b) => {
      b.bg('toneLight');
      b.photo(0, 0, 1080, 960);
      b.logo(40, 30, 220, 220);
      tx(b, 'tagline', 80, 990, 700, 70, { family: 'Caveat', weight: 700, size: 60, color: 'accentText' });
      tx(b, 'dishName', 80, 1064, 920, 160, {
        family: 'Poppins',
        weight: 600,
        size: 66,
        color: 'toneDark',
        maxLines: 2,
        leading: 1.08,
      });
      tx(b, 'price', 640, 1240, 360, 64, {
        family: 'Poppins',
        weight: 600,
        size: 52,
        color: 'accentText',
        align: 'right',
      });
    },
  },
  {
    id: 'fresh-story',
    family: 'fresh',
    slot: 'cutout',
    category: 'story',
    format: 'story',
    name: bi('Mercado fresco — historia', 'Fresh Market — story'),
    ad: { tagline: esOnly('recién hecho') },
    build: (b) => {
      b.bg('toneLight');
      tx(b, 'tagline', 90, 290, 900, 80, {
        family: 'Caveat',
        weight: 700,
        size: 70,
        color: 'accentText',
        align: 'center',
      });
      tx(b, 'dishName', 90, 372, 900, 200, {
        family: 'Poppins',
        weight: 600,
        size: 80,
        color: 'toneDark',
        align: 'center',
        maxLines: 2,
        leading: 1.08,
      });
      b.photo(60, 590, 960, 760, { fit: 'contain', zoom: 0.92, shadow: { ...SOFT, color: 'toneDark' } });
      tx(b, 'price', 290, 1372, 500, 76, {
        family: 'Poppins',
        weight: 600,
        size: 62,
        color: 'accentText',
        align: 'center',
      });
      b.logo(60, 1364, 172, 172, { onLight: true });
    },
  },
  {
    id: 'fresh-delivery',
    family: 'fresh',
    slot: 'full',
    category: 'delivery',
    format: 'delivery',
    name: bi('Mercado fresco — banner de delivery', 'Fresh Market — delivery banner'),
    ad: { tagline: esOnly('hecho hoy') },
    build: (b) => {
      b.bg('toneLight');
      b.photo(560, 0, 640, 800);
      b.logo(70, 30, 200, 200, { onLight: true });
      tx(b, 'tagline', 70, 230, 440, 60, { family: 'Caveat', weight: 700, size: 52, color: 'accentText' });
      tx(b, 'dishName', 70, 292, 460, 150, {
        family: 'Poppins',
        weight: 600,
        size: 58,
        color: 'toneDark',
        maxLines: 2,
        leading: 1.08,
      });
      tx(b, 'price', 70, 470, 400, 60, { family: 'Poppins', weight: 600, size: 46, color: 'accentText' });
    },
  },
  {
    id: 'fresh-new',
    family: 'fresh',
    slot: 'cutout',
    category: 'new',
    format: 'ig-square',
    name: bi('Mercado fresco — platillo nuevo', 'Fresh Market — new dish'),
    ad: { headline: esOnly('¡Nuevo!') },
    build: (b) => {
      b.bg('toneLight');
      b.photo(30, 260, 620, 790, { fit: 'contain', zoom: 0.92, shadow: { ...SOFT, color: 'toneDark' } });
      tx(b, 'headline', 700, 140, 330, 110, {
        family: 'Caveat',
        weight: 700,
        size: 100,
        color: 'accentText',
        align: 'right',
      });
      tx(b, 'dishName', 680, 520, 350, 220, {
        family: 'Poppins',
        weight: 600,
        size: 56,
        color: 'toneDark',
        align: 'right',
        maxLines: 3,
        leading: 1.08,
      });
      tx(b, 'price', 680, 770, 350, 64, {
        family: 'Poppins',
        weight: 600,
        size: 50,
        color: 'accentText',
        align: 'right',
      });
      b.logo(40, 30, 220, 220, { onLight: true });
    },
  },
  {
    id: 'fresh-menu',
    family: 'fresh',
    slot: 'full',
    category: 'menu',
    format: 'letter',
    name: bi('Mercado fresco — menú impreso', 'Fresh Market — printed menu'),
    build: (b) => {
      b.bg('toneLight');
      b.photo(-9, -9, 630, 219); // into the bleed on three sides (a trim error leaves no pale edge)
      b.text(60, 230, 492, 44, {
        role: 'restaurant',
        family: 'Poppins',
        weight: 600,
        size: 32,
        color: 'toneDark',
        maxLines: 1,
      });
      menuList(b, 8, 60, 290, 492, 46, {
        family: 'Poppins',
        size: 20,
        color: 'toneDark',
        price: 'accentText',
        rule: ['toneDark', 0.12],
      });
      b.text(60, 744, 380, 26, {
        role: 'contact',
        family: 'Poppins',
        size: 19,
        color: 'toneDark',
        maxLines: 1,
      });
      b.logo(452, 662, 100, 100, { onLight: true });
    },
  },

  // ════════════════════ PROMO — price first (Oswald) + Montserrat; no starbursts ════════════════════
  {
    id: 'promo-2for1',
    family: 'promo',
    slot: 'cutout',
    category: 'promo',
    format: 'ig-square',
    name: bi('Promoción — 2x1', 'Promo — 2 for 1'),
    ad: { headline: esOnly('2x1') },
    build: (b) => {
      b.bg('primary');
      tx(b, 'headline', 60, 40, 520, 280, {
        family: 'Oswald',
        weight: 700,
        size: 260,
        color: 'onPrimary',
        leading: 1,
      });
      tx(b, 'dishName', 60, 330, 360, 180, {
        family: 'Montserrat',
        weight: 700,
        size: 52,
        color: 'onPrimary',
        maxLines: 3,
        leading: 1.1,
      });
      tx(b, 'price', 60, 524, 360, 96, { family: 'Oswald', weight: 700, size: 84, color: 'onPrimary' });
      b.photo(430, 260, 650, 820, { fit: 'contain', zoom: 0.95, shadow: DEEP });
      b.logo(40, 840, 220, 220);
    },
  },
  {
    id: 'promo-combo',
    family: 'promo',
    slot: 'cutout',
    category: 'combo',
    format: 'ig-portrait',
    name: bi('Promoción — combo', 'Promo — combo'),
    ad: { headline: esOnly('Combo del día') },
    build: (b) => {
      b.bg('toneLight');
      tx(b, 'headline', 70, 70, 940, 70, {
        family: 'Montserrat',
        weight: 700,
        size: 52,
        color: 'accentText',
        caps: true,
        tracking: 100,
      });
      tx(b, 'dishName', 70, 150, 940, 150, {
        family: 'Oswald',
        weight: 600,
        size: 66,
        color: INK,
        maxLines: 2,
        leading: 1.08,
      });
      b.photo(40, 300, 1000, 800, { fit: 'contain', zoom: 0.95, shadow: SOFT });
      tx(b, 'price', 70, 1130, 640, 180, {
        family: 'Oswald',
        weight: 700,
        size: 170,
        color: 'accentText',
        leading: 1,
      });
      b.logo(800, 1110, 220, 220, { onLight: true });
    },
  },
  {
    id: 'promo-happy',
    family: 'promo',
    slot: 'full',
    category: 'happy',
    format: 'story',
    name: bi('Promoción — happy hour', 'Promo — happy hour'),
    ad: { headline: esOnly('Happy hour 5 a 7'), dishName: esOnly('Cerveza nacional') },
    build: (b) => {
      b.bg('secondary');
      b.photo(0, 0, 1080, 1920);
      shade(b, 900, 1020, 0.9, 'secondary');
      b.logo(60, 280, 240, 240);
      tx(b, 'headline', 60, 960, 960, 280, {
        family: 'Oswald',
        weight: 700,
        size: 130,
        color: 'onSecondary',
        caps: true,
        maxLines: 2,
        leading: 1,
      });
      tx(b, 'dishName', 60, 1260, 960, 60, {
        family: 'Montserrat',
        weight: 700,
        size: 44,
        color: 'onSecondarySmall',
      });
      b.rect(60, 1350, 420, 150, { fill: 'primary' });
      tx(b, 'price', 60, 1366, 420, 118, {
        family: 'Oswald',
        weight: 700,
        size: 100,
        color: 'onPrimary',
        align: 'center',
      });
    },
  },
  {
    id: 'promo-story',
    family: 'promo',
    slot: 'cutout',
    category: 'promo',
    format: 'story',
    name: bi('Promoción — historia con precio', 'Promo — story with price'),
    ad: { headline: esOnly('Solo hoy') },
    build: (b) => {
      b.bg('primary');
      tx(b, 'headline', 90, 290, 900, 90, {
        family: 'Montserrat',
        weight: 700,
        size: 60,
        color: 'onPrimary',
        align: 'center',
        caps: true,
        tracking: 160,
      });
      tx(b, 'dishName', 90, 384, 900, 190, {
        family: 'Oswald',
        weight: 600,
        size: 84,
        color: 'onPrimary',
        align: 'center',
        maxLines: 2,
        leading: 1.06,
      });
      b.photo(40, 560, 1000, 760, { fit: 'contain', zoom: 0.95, shadow: DEEP });
      tx(b, 'price', 260, 1320, 560, 130, {
        family: 'Oswald',
        weight: 700,
        size: 120,
        color: 'onPrimary',
        align: 'center',
        leading: 1,
      });
      b.logo(60, 1364, 172, 172);
    },
  },
  {
    id: 'promo-delivery',
    family: 'promo',
    slot: 'cutout',
    category: 'promo',
    format: 'delivery',
    name: bi('Promoción — descuento para delivery', 'Promo — delivery discount'),
    build: (b) => {
      b.bg('white');
      b.logo(60, 24, 200, 200, { onLight: true });
      tx(b, 'dishName', 60, 236, 520, 150, {
        family: 'Oswald',
        weight: 600,
        size: 64,
        color: INK,
        maxLines: 2,
        leading: 1.06,
      });
      tx(b, 'oldPrice', 60, 400, 260, 56, { family: 'Montserrat', weight: 400, size: 40, color: MUTED });
      b.line(60, 430, 238, 430, MUTED, 3);
      tx(b, 'price', 60, 460, 520, 150, {
        family: 'Oswald',
        weight: 700,
        size: 140,
        color: 'accentText',
        leading: 1,
      });
      b.photo(540, 30, 640, 740, { fit: 'contain', zoom: 0.92, shadow: SOFT });
    },
  },
  {
    id: 'promo-price',
    family: 'promo',
    slot: 'full',
    category: 'promo',
    format: 'ig-square',
    name: bi('Promoción — precio especial', 'Promo — special price'),
    build: (b) => {
      b.bg('secondary');
      b.photo(0, 0, 1080, 1080);
      shade(b, 560, 520, 0.85, 'secondary');
      b.logo(40, 30, 220, 220);
      tx(b, 'dishName', 60, 860, 600, 150, {
        family: 'Oswald',
        weight: 600,
        size: 66,
        color: 'onSecondary',
        maxLines: 2,
        leading: 1.06,
      });
      b.rect(700, 800, 380, 280, { fill: 'primary' });
      tx(b, 'oldPrice', 700, 830, 380, 50, {
        family: 'Montserrat',
        weight: 400,
        size: 36,
        color: 'onPrimarySmall',
        align: 'center',
      });
      b.line(810, 856, 970, 856, 'onPrimarySmall', 3);
      tx(b, 'price', 700, 890, 380, 140, {
        family: 'Oswald',
        weight: 700,
        size: 110,
        color: 'onPrimary',
        align: 'center',
        leading: 1,
      });
    },
  },
  {
    id: 'promo-board',
    family: 'promo',
    slot: 'cutout',
    category: 'promo',
    format: 'board-h',
    name: bi('Promoción — pantalla 2x1', 'Promo — 2 for 1 board'),
    ad: { headline: esOnly('2x1') },
    build: (b) => {
      b.bg('primary');
      b.photo(40, 60, 1000, 960, { fit: 'contain', zoom: 0.95, shadow: DEEP });
      tx(b, 'headline', 1100, 120, 760, 330, {
        family: 'Oswald',
        weight: 700,
        size: 320,
        color: 'onPrimary',
        leading: 1,
      });
      tx(b, 'dishName', 1100, 500, 760, 200, {
        family: 'Montserrat',
        weight: 700,
        size: 76,
        color: 'onPrimarySmall',
        maxLines: 2,
        leading: 1.1,
      });
      tx(b, 'price', 1100, 740, 520, 150, {
        family: 'Oswald',
        weight: 700,
        size: 130,
        color: 'onPrimary',
        leading: 1,
      });
      b.logo(1640, 820, 240, 240);
    },
  },
];
